import React from "react";
import {Container} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import {Fold, Pages} from "../../components/layout";
import sparkLogo from "../../assets/images/logos/spark-logo.svg";
import notebookExample from "../../assets/images/screenshots/spark/notebook-example.png";
import Header from "../../components/Header";
import StandardIcons from "../../components/layout/StandardIcons";
import CallMeBackForm from "./CallMeBackForm";

/**
 * Renders the SparkELN component, which serves as the main layout for presenting
 * information about the Spark Electronic Lab Notebook (ELN) software. The component
 * includes an introduction to Spark's features, targeted to both researchers and educators,
 * as well as sections encouraging user engagement and early sign-up access.
 *
 * @return {JSX.Element} The rendered SparkELN component, structured into sections
 *                       that provide an overview of Spark and SparkED features,
 *                       along with visuals and user interaction elements.
 */
export default function SparkELN() {
  return (
    <Pages.Base padded={false}>
      <Fold>
        <Fold.Main className="bg-darkest">
          <div className="text-center text-white">
            <img
              src={sparkLogo}
              height={120}
              alt={`Spark Logo`}
              className="mb-2"
            />
            <h1 className="mb-1">Spark</h1>
            <div className="text-xl fw-bold mb-5 gray-400" aria-roledescription="slogan">
              The ELN that actually works for you
            </div>
            <img
              src={notebookExample}
              alt={`Spark ELN software screenshot`}
              className="d-block mx-auto rounded-3 border border-1 border-dark shadow-lg"
              style={{ maxHeight: 'min(50vh, 600px)', maxWidth: '90vw' }}
            />
          </div>
        </Fold.Main>
      </Fold>
      <section className="py-5 bg-white mt-5">
        <Container>
          <Row>
            <Col sm={{ span: 5, offset: 1 }}>
              <StandardIcons.Research fontSize={70} className="mb-4" />
              <Header component="h2">Spark: for researchers</Header>
              <p>
                Spark is a powerful ELN that can be used for a wide range of research projects.
                It offers a wide range of features, including:
              </p>
              <ul>
                <li>
                  <strong>File-folder structure</strong>
                  <p className="text-md gray-600">Similar to Windows Explorer or Mac Finder.</p>
                </li>
                <li>
                  <strong>Reusable protocols</strong>
                  <p className="text-md gray-600">
                    Create and share reusable protocols that can be easily adapted to different
                    projects.
                  </p>
                </li>
                <li>
                  <strong>Import from MS Word, PDF, and more</strong>
                  <p className="text-md gray-600">
                    Import your current data from Microsoft Word or PDF files.
                  </p>
                </li>
                <li>
                  <strong>Integrated spreadsheets</strong>
                  <p className="text-md gray-600">
                    Create and edit spreadsheets directly in the ELN.
                  </p>
                </li>
                <li>
                  <strong>Versioning</strong>
                  <p className="text-md gray-600">Track every change and revert to any previous version</p>
                </li>
                <li>
                  <strong>Sharing</strong>
                  <p className="text-md gray-600">
                    Share files or entire folders with the familiar permission options (view-only or edit, etc.)
                  </p>
                </li>
              </ul>
            </Col>
            <Col sm={{ span: 5 }}>
              <StandardIcons.Education fontSize={70} className="mb-4" />
              <Header component="h2">SparkED: for educators</Header>
              <p>
                SparkED is a powerful ELN that can be used for a wide range of educational projects.
                It offers a wide range of features, including:
              </p>
              <ul>
                <li>
                  <strong>Create courses</strong>
                  <p className="text-md gray-600">
                    Create courses
                  </p>
                </li>
                <li>
                  <strong>Clone courses</strong>
                  <p className="text-md gray-600">From your own previous courses, or publicly available courses.</p>
                </li>
                <li>
                  <strong>Invite students by mail or link</strong>
                  <p className="text-md gray-600">
                    Manually add students via email or let students sign up themselves via a link.
                  </p>
                </li>
                <li>
                  <strong>Review with highlights</strong>
                  <p className="text-md gray-600">
                    Review students' progress and assignments. Highlighting the differences
                    between the student's and the teacher's version of the course.
                  </p>
                </li>
              </ul>
            </Col>
          </Row>
        </Container>
      </section>
      <section className="py-5 bg-lighter">
        <Container className="text-center">
          <Header component="h2">Looking for a smoother ELN experience?</Header>
          <p className="mb-5">
            Sign up below and get early access.
          </p>
          <div style={{ maxWidth: '350px', margin: '0 auto' }}>
            <CallMeBackForm />
          </div>
        </Container>
      </section>
    </Pages.Base>
  )
}
